import { apiErrorFromResponse } from '@multaqa/api-client'
import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { api, apiOrigin, fetchWithSessionActivity } from '@/lib/api'
import { downloadFile } from '@/lib/download'
import type {
  Account,
  DrillDownLine,
  ReportName,
  ReportQuery,
  SavedReport,
  SavedReportInput,
} from './api'

function unwrap<T>(wrapper: { data?: unknown }, endpoint: string): T {
  if (wrapper.data === undefined || wrapper.data === null) {
    throw new Error(`Malformed ${endpoint} response: missing data envelope.`)
  }
  return wrapper.data as T
}

export function useDrillDownLines(
  companyId: string | null,
  lineIds: string[],
  options?: Omit<UseQueryOptions<DrillDownLine[], Error>, 'queryKey' | 'queryFn'>,
) {
  const key = [...lineIds].sort().join('|')
  return useQuery<DrillDownLine[], Error>({
    queryKey: ['companies', companyId, 'accounting', 'reports', 'drill-down', key] as const,
    enabled: Boolean(companyId && lineIds.length > 0),
    ...options,
    queryFn: async (): Promise<DrillDownLine[]> => {
      if (!companyId || lineIds.length === 0) return []
      const params = new URLSearchParams()
      for (const id of lineIds) params.append('ids[]', id)
      const response = await fetchWithSessionActivity(
        `${apiOrigin}/api/accounting/journal-lines/drill-down?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'X-Company-Id': companyId },
        },
      )
      if (!response.ok) throw await apiErrorFromResponse(response)
      return unwrap<DrillDownLine[]>((await response.json()) as { data?: unknown }, 'drill-down')
    },
  })
}

export function useAccounts(
  companyId: string | null,
  options?: Omit<UseQueryOptions<Account[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<Account[], Error>({
    queryKey: ['companies', companyId, 'accounting', 'accounts'] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<Account[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/accounts', {
        headers: { 'X-Company-Id': companyId },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load accounts')
      return unwrap<Account[]>(response.data ?? {}, 'accounts') ?? []
    },
  })
}

export type ExportFormat = 'csv' | 'pdf'

export function reportExportUrl(
  report: ReportName,
  format: ExportFormat,
  query: ReportQuery,
): string {
  const params = new URLSearchParams()
  params.set('format', format)
  if (query.from !== undefined) params.set('from', query.from)
  if (query.to !== undefined) params.set('to', query.to)
  if (query.as_at !== undefined) params.set('as_at', query.as_at)
  if (query.cost_center_id !== undefined) params.set('cost_center_id', query.cost_center_id)
  for (const id of query.cost_center_ids ?? []) params.append('cost_center_id[]', id)
  for (const id of query.analysis_tag_ids ?? []) params.append('analysis_tag_ids[]', id)
  for (const id of query.journal_ids ?? []) params.append('journal_ids[]', id)
  if (query.group_by !== undefined) params.set('group_by', query.group_by)
  if (query.compare_prior === true) params.set('compare_prior', '1')
  if (query.side !== undefined) params.set('side', query.side)
  if (query.party_type !== undefined) params.set('party_type', query.party_type)
  if (query.party_id !== undefined) params.set('party_id', query.party_id)
  if (query.account_id !== undefined) params.set('account_id', query.account_id)
  if (query.doc_type !== undefined) params.set('doc_type', query.doc_type)
  return `${apiOrigin}/api/accounting/reports/${report}/export?${params.toString()}`
}

export async function exportReport(
  companyId: string,
  report: ReportName,
  format: ExportFormat,
  query: ReportQuery,
): Promise<void> {
  await downloadFile(reportExportUrl(report, format, query), `${report}.${format}`, {
    'X-Company-Id': companyId,
  })
}

export function useSavedReports() {
  return useQuery<SavedReport[], Error>({
    queryKey: ['accounting', 'saved-reports'],
    queryFn: async () => {
      const response = await api.GET('/accounting/saved-reports')
      if (response.error) throw new Error(response.error.message ?? 'Failed to load saved reports')
      return unwrap<SavedReport[]>(response.data ?? {}, 'saved-reports')
    },
  })
}

export async function saveReport(input: SavedReportInput): Promise<SavedReport> {
  const response = await api.POST('/accounting/saved-reports', { body: input })
  if (response.error) throw new Error(response.error.message ?? 'Failed to save report')
  return unwrap<SavedReport>(response.data ?? {}, 'saved-reports')
}

export async function updateSavedReport(id: string, input: SavedReportInput): Promise<SavedReport> {
  const response = await api.PATCH('/accounting/saved-reports/{savedReport}', {
    params: { path: { savedReport: id } },
    body: input,
  })
  if (response.error) throw new Error(response.error.message ?? 'Failed to update saved report')
  return unwrap<SavedReport>(response.data ?? {}, 'saved-reports')
}
